import { TICK_MS, type Mover, type MonsterArchetype, type Vec2, distance, inBounds } from '@d2/core';
import { type BodyAction, type GroundKind } from '@d2/schema';

import {
  type ClockEstimate,
  createClock,
  observe,
  serverTickAt,
  serverTimeAt,
  tickObservation,
} from './clock';
import {
  type InterpolationBuffer,
  createBuffer,
  newestTick,
  positionAt,
  receiveSample,
  tickTimeMs,
} from './interpolation';
import { placeAt } from './navigation';
import {
  type AuthoritativeState,
  type PendingIntent,
  type Prediction,
  advancePrediction,
  createPrediction,
  predictMoveTo,
  predictedPosition,
  reconcile,
} from './prediction';

/**
 * Everybody on the floor, one snapshot at a time. **The composition, and
 * nothing else.**
 *
 * Four modules already answer the four questions this milestone had: `./clock`
 * maps the browser's clock onto the server's timeline, `./interpolation` draws
 * an entity whose input this client does not hold, `./prediction` draws the one
 * it does, and `./navigation` composes core's routing. Each of them is pure,
 * takes time as a parameter, and says in as many words that wiring it up is
 * somebody else's job (`TODO.md` #160, #162, #183, #191). This is that job, and
 * it is deliberately the *whole* of it: not one movement rule, interpolation
 * rule or clock rule is restated here, because every one of them already has
 * exactly one definition and a second would be the bug this architecture exists
 * to prevent.
 *
 * It is a value, folded like the modules it composes: a `Session` in, a
 * `Session` out, no clock read anywhere and no socket in sight. That is what
 * lets a whole minute of a four-player room be replayed in a unit test, and it
 * is why the Colyseus connection (`./room-link`) can be a thin adapter that
 * decodes bytes and calls two functions.
 *
 * ## The one decision this file makes: who is drawn how, and by name
 *
 * **The local player runs through prediction; every other entity runs through
 * its own interpolation buffer, and the two are told apart by session id.**
 * Not by "the first entity to arrive", not by leaving the local buffer quietly
 * unread — by identity, because `TODO.md` #162 is explicit about what the
 * alternative costs: *"an interpolated local player is a 100 ms input delay,
 * which is exactly what prediction exists to avoid"*. The id is the room's own
 * `sessionId`, which is also what `WorldState.players` is keyed by, so the
 * renderer's arrival-index ids (`TODO.md` #122, #150) are gone rather than
 * reconciled.
 *
 * ## Entity lifecycle is reconstruction, not bookkeeping
 *
 * `receiveSnapshot` builds a *fresh* map of buffers from the players the
 * snapshot actually contains. A player who joined has no buffer, so one is
 * created; a player who left is simply not copied across, and the sprite the
 * renderer had for them disappears with them on the next frame. There is no
 * `onAdd`/`onRemove` pair to get wrong and no way for the two halves to drift,
 * which matters more than it looks: `TODO.md` #201 notes that a recycled entity
 * id would otherwise inherit a dead entity's samples and interpolate from
 * wherever *that* body was standing. A buffer that only exists while its player
 * is in the snapshot cannot do that.
 *
 * ## Monsters are the same fold, in a second map
 *
 * A floor now carries sixty bodies nobody predicts. Every one of them is
 * interpolated exactly as a remote player is — the same buffer, the same delay,
 * the same reconstruction — because that is not one of two options for a monster
 * but the *only* source of position it has: no client holds its input and no
 * client re-derives its flow field (`packages/schema/src/monsters.ts`). A death
 * therefore needs no message and no special case: the room stops replicating the
 * body, the next snapshot does not mention it, and it is gone from here and from
 * the renderer on the next frame, exactly as a player who left is.
 *
 * They are kept in a second map rather than mixed into `remotes` for the same
 * reason the wire keeps two: the ids come from different authorities (a Colyseus
 * session id and a room-chosen monster id), and a monster carries a description
 * — archetype, life, elite standing — that a player has no equivalent of. One
 * map would make a collision between the two namespaces a silent redraw of the
 * wrong body.
 *
 * ## What a monster *is* is not interpolated, only where it is
 *
 * The buffer carries positions; the archetype, the life band and the elite
 * standing are taken from the newest snapshot and shared, unchanged, into every
 * frame drawn from it. Two consequences, both deliberate: a bar can be up to the
 * interpolation delay ahead of the body it belongs to, which is a fifth of a
 * second on a value that moves sixteen times in a monster's whole life
 * (`TODO.md` #281); and the identity object is allocated once per snapshot
 * rather than once per frame, which at sixty bodies is the difference between
 * twelve hundred and thirty-six hundred allocations a second.
 *
 * ## The prediction's clock is the server's, exactly
 *
 * `advanceSession` does not spend the frame's own duration on the prediction.
 * It asks `./clock` what tick the server is on and spends whatever whole ticks
 * the prediction owes to catch up to it — so `Prediction.tick` *is*
 * `serverTickAt`, frame after frame, which is precisely what `TODO.md` #183 and
 * #191 say has to be true before a snapshot for tick *T* can honestly be
 * compared against what this client thought at *T*.
 *
 * Two consequences worth stating. A prediction that fell behind — a sleeping
 * tab, a capped catch-up — is walked back into step by the ordinary frame loop
 * rather than by a special case, because the gap it owes simply persists across
 * frames until `MAX_CATCHUP_STEPS` at a time has closed it. And with no clock
 * yet (nothing has been observed, so nothing has been received), the frame's own
 * duration is spent instead: a client with no room has no timeline but its own,
 * and owes nothing at all for a frame that never happened.
 *
 * Positions are in **cell coordinates**, like all of core. The renderer
 * projects; no pixel constant belongs in here.
 */

/**
 * One replicated player, as the client reads it.
 *
 * `destination` folds the wire's `walking` plus `destinationX/Y` into the one
 * fact both consumers want — the cell this walk ends on, or `null` when the
 * server is not walking this entity — which is also where the deliberate
 * staleness of `destinationX/Y` after a walk ends is absorbed (`state.ts`,
 * `clearDestination`). The mapping from `PlayerState` is `./wire`'s.
 */
export interface PlayerSnapshot {
  /** Colyseus session id. The key everything about this entity is filed under. */
  readonly id: string;
  /** Continuous position in cell coordinates. Truth. */
  readonly position: Vec2;
  /** The cell the server accepted, or `null` when this entity is not walking. */
  readonly destination: Vec2 | null;
  /** The sequence of the last intent the server applied to this entity. */
  readonly lastSequence: number;
  /**
   * What this seat is **doing** — the visual reading of a truth `destination`
   * and the wire's own `dead` flag report mechanically.
   *
   * A name rather than a code, read at the edge through `bodyActionOf`
   * (`./wire`), so nothing downstream knows a list order. It rides beside the
   * position rather than in a channel of its own because it arrives in the same
   * patch and is drawn in the same frame: `action === 'dead'` if and only if the
   * seat is dead, and `action === 'walking'` only when it is walking, and both
   * agreements are held by the wire (`packages/schema/src/actions.ts`) rather
   * than re-checked here.
   */
  readonly action: BodyAction;
}

/**
 * What one monster *is*, as opposed to where it is.
 *
 * Everything in here is a value the wire sends and the renderer reads, and
 * nothing in here is a rule: the champion tier and the affixes are **ids**, the
 * client's half of the reference `packages/schema/src/monsters.ts` describes,
 * and what they mean stays in `@d2/core`.
 */
export interface MonsterIdentity {
  /**
   * Whether the server has this body part way along a step.
   *
   * The **mechanical** fact, which is what an interpolator reads. `action` is
   * the visual reading of the same truth and is the one the renderer draws — a
   * body walking towards something it is mid-swing at is both, and the wire
   * says the more specific of the two (`packages/schema/src/actions.ts`).
   */
  readonly walking: boolean;
  /**
   * What this body is **doing**: idle, walking, striking, blocking, hurt,
   * casting.
   *
   * Never `dead`. A monster's death is a despawn — the room deletes the map
   * entry on the tick the body is reaped — so the code cannot travel for one,
   * and a corpse no despawn ever took away is a state the simulation cannot
   * produce (`setMonsterAction` refuses it at the wire).
   *
   * It rides in the identity rather than beside the position because it is
   * *what a monster is* as of the newest snapshot, exactly like the life band:
   * shared into every frame drawn from that snapshot, so sixty bodies cost sixty
   * identity objects a snapshot rather than sixty a frame.
   */
  readonly action: BodyAction;
  /** Melee, ranged or caster — the only classification the design gives a body. */
  readonly archetype: MonsterArchetype;
  /**
   * How much life is left, in `0..1`.
   *
   * Read through `lifeFraction` at the edge (`./wire`), so nothing downstream
   * divides by `LIFE_BANDS` or has to know which way the band rounded. Any life
   * at all is greater than zero, so an empty bar means dead and nothing else.
   */
  readonly life: number;
  /** The champion tier id, or `null` for a body that is not a champion. */
  readonly champion: string | null;
  /** The elite affix ids this body carries, in the wire's bit order. */
  readonly affixes: readonly string[];
  /** True for the boss of a unique pack — the one thing the affix mask cannot say. */
  readonly leader: boolean;
}

/** One replicated monster, as the client reads it. The mapping from `MonsterState` is `./wire`'s. */
export interface MonsterSnapshot extends MonsterIdentity {
  /** The room's own id for this body, which is also the key `WorldState.monsters` uses. */
  readonly id: string;
  /** Continuous position in cell coordinates. Truth, and the only source of one a monster has. */
  readonly position: Vec2;
}

/**
 * One thing lying on the floor, as the client reads it. The mapping from
 * `GroundItemState` is `./wire`'s.
 *
 * Everything in here is a value the wire sends and the renderer reads, and
 * nothing in here is a rule — the same discipline `MonsterIdentity` keeps. The
 * rarity is an **id** rather than a colour or a table row, so what a tier is
 * worth stays in `@d2/core`; what a client does with it is pick a sprite and a
 * label.
 *
 * There is no position, only a **cell**: a drop is placed on one and never
 * moves, which is also why it needs no interpolation buffer and why the render
 * time cannot change where it is drawn.
 */
export interface GroundItemSnapshot {
  /** The room's own id for this drop — the key `WorldState.ground` uses, and the one `pickUp` names. */
  readonly id: string;
  /** The cell it lies on. Whole numbers; the renderer projects. */
  readonly cell: Vec2;
  /** Which of the four channels it came out of — gear, potion, gold or a respec scroll. */
  readonly kind: GroundKind;
  /** The tier id, or `null` for the three kinds that have no tier at all. */
  readonly rarity: string | null;
  /** The base it dropped from — a row in one of core's own tables. Empty for gold. */
  readonly baseId: string;
  /** The coins in this pile, or zero for anything that is not one. */
  readonly gold: number;
}

/**
 * One tick of the room, as the client reads it.
 *
 * `WorldState.tick` dates the whole snapshot — no player, monster or drop
 * carries a timestamp of its own — so it is the stamp on every sample filed from
 * it and the one observation the clock estimator is fed.
 */
export interface WorldSnapshot {
  readonly tick: number;
  readonly players: readonly PlayerSnapshot[];
  readonly monsters: readonly MonsterSnapshot[];
  /** Everything lying on the floor. Rebuilt per snapshot, exactly as the two lists above are. */
  readonly ground: readonly GroundItemSnapshot[];
}

/**
 * What the server says about one entity, on the tick a snapshot was produced —
 * or `null` when that entity is not in it.
 *
 * The join case: a client is handed a full snapshot the instant it joins, and
 * its own entry in it is where the server put its body. Seeding the prediction
 * from that rather than from a guess is what stops the first frame drawing the
 * player at an invented position and then snapping it to the spawn.
 */
export const stateOf = (snapshot: WorldSnapshot, id: string): AuthoritativeState | null => {
  const player = snapshot.players.find((candidate) => candidate.id === id);

  return player === undefined
    ? null
    : {
        tick: snapshot.tick,
        position: player.position,
        destination: player.destination,
        lastSequence: player.lastSequence,
      };
};

/** One monster being tracked: where it has been, and what the room last said it is. */
export interface MonsterTrack {
  /** What it is, as of the newest snapshot. Shared into every frame drawn from that snapshot. */
  readonly identity: MonsterIdentity;
  /** Its recent positions. The only source of position a monster has. */
  readonly buffer: InterpolationBuffer;
}

/**
 * The client's whole view of the floor.
 *
 * Immutable, like everything it composes: every function here returns a new
 * session, so a frame can never observe a half-applied update.
 */
export interface Session {
  /** This client's Colyseus session id — the one entity that is predicted rather than interpolated. */
  readonly localId: string;
  /** The body every entity on this floor walks with, and the field it is measured against. */
  readonly mover: Mover;
  /** Where the browser's clock sits on the server's timeline. */
  readonly clock: ClockEstimate;
  /** The local player's predicted movement, and everything needed to reconcile it. */
  readonly prediction: Prediction;
  /** One buffer per remote entity, keyed by session id. The local player is never in here. */
  readonly remotes: ReadonlyMap<string, InterpolationBuffer>;
  /** One track per monster, keyed by the room's own monster id. */
  readonly monsters: ReadonlyMap<string, MonsterTrack>;
  /**
   * What every **player** on this floor is doing, keyed by session id — the
   * local one included.
   *
   * A map of its own rather than a field on the buffers, for two reasons. The
   * local player has no buffer at all (it is predicted, `TODO.md` #162) and
   * still has to be drawn dead, so a per-buffer field could not describe the one
   * seat that matters most to its own player. And an action is not
   * interpolated: it is the newest word, drawn as it stands, the same treatment
   * a monster's life band and elite standing already get.
   *
   * Rebuilt per snapshot like every other map here, so a player who left takes
   * their action with them and no stale corpse can outlive the seat that had it.
   */
  readonly actions: ReadonlyMap<string, BodyAction>;
  /**
   * Everything lying on the floor, keyed by the room's own drop id.
   *
   * No buffer and no track: a drop has one cell for its whole life, so the
   * newest snapshot *is* the answer and there is nothing between two of them to
   * interpolate. Rebuilt per snapshot like the other two maps, which is what
   * makes a pickup — anybody's — a drop that simply stops being mentioned.
   */
  readonly ground: ReadonlyMap<string, GroundItemSnapshot>;
}

export interface SessionInit {
  readonly localId: string;
  readonly mover: Mover;
  /** Where the local player starts, on whatever tick the caller knows about. */
  readonly spawn: AuthoritativeState;
  /** Cells per second. Core's `DEFAULT_MOVE_SPEED` unless a caller says otherwise. */
  readonly speed?: number;
}

/** A client that has joined and heard nothing yet. */
export const createSession = ({ localId, mover, spawn, speed }: SessionInit): Session => ({
  localId,
  mover,
  clock: createClock(),
  prediction: createPrediction(mover, spawn, speed),
  remotes: new Map(),
  monsters: new Map(),
  actions: new Map(),
  ground: new Map(),
});

/** What a monster is, taken off a snapshot entry and kept for as long as that snapshot is the newest. */
const identityOf = ({
  walking,
  archetype,
  life,
  champion,
  affixes,
  leader,
  action,
}: MonsterSnapshot): MonsterIdentity => ({
  walking,
  archetype,
  life,
  champion,
  affixes,
  leader,
  action,
});

/**
 * Folds one snapshot in: the clock, the local player, everybody else, and the
 * floor's population.
 *
 * Four things happen, in the order they depend on each other. The tick becomes
 * a clock observation, because every later query — the render time the buffers
 * are read at, the tick the prediction is anchored to — is derived from it. The
 * local player's entry is reconciled into the prediction, which is where a
 * disagreement between two machines running identical code becomes visible.
 * Every other player's position is filed as a sample, in a map rebuilt from this
 * snapshot alone. And every monster's is filed the same way, in a map of its
 * own, alongside the newest word on what that monster is.
 *
 * The work is one pass over each list and one map insert per body: sixty
 * monsters cost sixty samples and sixty buffers, and nothing here is quadratic
 * in the population — no body is compared against another, and the map that goes
 * out is built rather than diffed.
 */
export const receiveSnapshot = (session: Session, snapshot: WorldSnapshot, localTimeMs: number): Session => {
  const clock = observe(session.clock, tickObservation(snapshot.tick, localTimeMs));
  const remotes = new Map<string, InterpolationBuffer>();
  const monsters = new Map<string, MonsterTrack>();
  const actions = new Map<string, BodyAction>();
  let prediction = session.prediction;

  for (const player of snapshot.players) {
    // Before the local branch returns, and for every seat alike: the local
    // player's entry is reconciled rather than filed, so an action taken inside
    // the remote branch would draw everybody's corpse except this client's own.
    actions.set(player.id, player.action);

    if (player.id === session.localId) {
      prediction = reconcile(session.mover, prediction, {
        tick: snapshot.tick,
        position: player.position,
        destination: player.destination,
        lastSequence: player.lastSequence,
      });
      continue;
    }

    remotes.set(
      player.id,
      receiveSample(session.remotes.get(player.id) ?? createBuffer(), {
        tick: snapshot.tick,
        position: player.position,
      }),
    );
  }

  for (const monster of snapshot.monsters) {
    monsters.set(monster.id, {
      identity: identityOf(monster),
      buffer: receiveSample(session.monsters.get(monster.id)?.buffer ?? createBuffer(), {
        tick: snapshot.tick,
        position: monster.position,
      }),
    });
  }

  // Replaced wholesale rather than merged. A drop is the one thing on this floor
  // with no history worth keeping — one cell for its whole life — and the entry
  // *can* change while it lies there, because a capped purse leaves the rest of
  // a pile behind (`descent.room.ts`). Taking the newest word is therefore both
  // the cheapest and the only correct reading.
  const ground = new Map(snapshot.ground.map((item) => [item.id, item]));

  return { ...session, clock, prediction, remotes, monsters, actions, ground };
};

/**
 * Spends time on the local player's prediction, in whole simulation steps.
 *
 * The number of steps comes from the *clock*, not from the frame: whatever tick
 * the server is on at `localTimeMs`, the prediction owes the difference, and
 * `advancePrediction` banks it into whole 50 ms steps and caps a stall at
 * `MAX_CATCHUP_STEPS` exactly as it would a jittery frame. That is what keeps
 * `Prediction.tick` equal to `serverTickAt` rather than merely parallel to it,
 * and a client whose history is filed under the server's own tick numbers is
 * the whole of `TODO.md` #183.
 *
 * `frameMs` is the fallback and only the fallback: with nothing observed there
 * is no server timeline to be on, so the world advances by however long this
 * frame was. The two are not interchangeable, and the difference is the point.
 * A connected client that stalled *owes* the ticks the server took meanwhile,
 * because the server took them; a client with no room owes nothing for a frame
 * that never happened, because nothing simulated it.
 */
export const advanceSession = (session: Session, localTimeMs: number, frameMs: number): Session => {
  const target = serverTickAt(session.clock, localTimeMs);
  const elapsedMs =
    target === null ? Math.max(0, frameMs) : Math.max(0, target - session.prediction.tick) * TICK_MS;

  return { ...session, prediction: advancePrediction(session.prediction, elapsedMs) };
};

/** One monster, ready to draw: where it is this frame, and what it is. */
export interface DrawnMonster {
  readonly position: Vec2;
  /**
   * Shared with the session's track rather than copied — see the note above on
   * why identity is rebuilt at the snapshot rate and not at the frame rate.
   */
  readonly identity: MonsterIdentity;
}

/** Where everybody is, at one reading of the browser's clock. */
export interface EntityPositions {
  /** The local player, predicted — never a buffer, never 100 ms late. */
  readonly local: Vec2;
  /** Everybody else, interpolated, keyed by session id. */
  readonly remotes: ReadonlyMap<string, Vec2>;
  /** The floor's population, interpolated the same way, keyed by monster id. */
  readonly monsters: ReadonlyMap<string, DrawnMonster>;
  /**
   * What each **player** is doing, keyed by session id, the local one included.
   *
   * Handed straight out of the session rather than resolved against the clock,
   * for the reason the floor's drops are: an action is not interpolated. It is
   * the newest thing the server said, drawn as it stands — a corpse a hundred
   * milliseconds late is a corpse drawn swinging.
   *
   * A body with no entry is one nothing has described, which `action-art.ts`
   * draws as idle: that is the wire's own default (`IDLE_ACTION` is zero) and
   * the state a sandbox-placed entity is genuinely in.
   */
  readonly actions: ReadonlyMap<string, BodyAction>;
  /**
   * Everything lying on the floor, keyed by drop id.
   *
   * Handed straight out of the session rather than resolved against the clock,
   * because a drop does not move. It rides in here rather than in a second
   * question so that a frame — and the click that lands in one — reads one
   * answer about what is on the floor and what is standing on it.
   */
  readonly ground: ReadonlyMap<string, GroundItemSnapshot>;
}

/**
 * Where one interpolated body is at a render time — `null` when nothing has been
 * heard from it yet.
 *
 * Shared by every entity this client does not predict, which is now sixty-three
 * of the sixty-four on a full floor. Two rules, both `interpolation.ts`'s: an
 * entity with no samples is not drawn at all, because inventing an origin would
 * flash it through cell (0, 0); and with no clock estimate the newest sample is
 * drawn exactly, which is what makes a placement in the renderer sandbox land
 * where it was put rather than nowhere.
 */
const drawnAt = (buffer: InterpolationBuffer, renderTimeMs: number | null): Vec2 | null => {
  const newest = newestTick(buffer);

  return newest === null ? null : positionAt(buffer, renderTimeMs ?? tickTimeMs(newest) + buffer.delayMs);
};

/**
 * Where to draw every entity at `localTimeMs`.
 *
 * Pure, and one clock reading for the whole frame: the local player comes
 * straight off the prediction and every remote and every monster is read out of
 * its own buffer at the *same* mapped render time, so nothing on screen can be a
 * frame out of step with anything else.
 *
 * One pass per body, and the per-body work is a binary-free scan of at most
 * `bufferedSampleLimit()` samples — eight at the default delay — so a full floor
 * is a few hundred comparisons a frame and no allocation beyond the answer
 * itself.
 */
export const positionsAt = (session: Session, localTimeMs: number): EntityPositions => {
  const renderTimeMs = serverTimeAt(session.clock, localTimeMs);
  const remotes = new Map<string, Vec2>();
  const monsters = new Map<string, DrawnMonster>();

  for (const [id, buffer] of session.remotes) {
    const position = drawnAt(buffer, renderTimeMs);

    if (position !== null) remotes.set(id, position);
  }

  for (const [id, track] of session.monsters) {
    const position = drawnAt(track.buffer, renderTimeMs);

    if (position !== null) monsters.set(id, { position, identity: track.identity });
  }

  return {
    local: predictedPosition(session.prediction),
    remotes,
    monsters,
    actions: session.actions,
    ground: session.ground,
  };
};

/** A click: the session that has already walked it, and the intent to put on the wire. */
export interface SessionIntent {
  readonly session: Session;
  /** The intent to send, or `null` when the click named a cell this floor does not have. */
  readonly intent: PendingIntent | null;
}

/**
 * "Walk there."
 *
 * The walk starts on this frame and the intent is handed back for the caller to
 * send, numbered so the server's answer can be matched to the click that asked
 * for it. A click with **no route** — rock, a sealed cavern, a gap this body
 * does not fit — is still numbered and still sent, and is predicted as the walk
 * as close as it can get, because that is what the room does with it
 * (`world.ts`'s `orderMove`, `TODO.md` #92) and predicting anything else would
 * be the client disagreeing with truth on purpose.
 *
 * A click **off the map** is the one exception, and it is not a second rule: it
 * is the same bounds check `intents.ts` already makes, made on the side that
 * can avoid the round trip. The server refuses an off-map target outright — *"no
 * camera ever showed the player that cell, so the coordinate was fabricated"* —
 * so a client that predicted one would be predicting a walk that is certain to
 * be corrected, and spending an intent allowance to find that out. `null` here
 * means nothing was walked and nothing is sent.
 */
export const orderMoveTo = (session: Session, target: Vec2): SessionIntent => {
  if (!inBounds(session.mover.clearance.grid, target)) return { session, intent: null };

  const predicted = predictMoveTo(session.mover, session.prediction, target);

  return { session: { ...session, prediction: predicted.prediction }, intent: predicted.intent };
};

/** A sequence number taken off the counter, and the session that has spent it. */
export interface SessionSequence {
  readonly session: Session;
  /** The number to put on the intent. Already spent — the next caller gets the one after it. */
  readonly sequence: number;
}

/** The floor a descent has put this client on. */
export interface FloorArrival {
  /**
   * The body that walks the new floor: a clearance field eroded from the new
   * grid, and the very mover `./cave` verified the new floor's stairs against.
   *
   * It has to be replaced rather than kept, and not only because it would be
   * measuring the wrong map: core throws when a mover and a grid disagree about
   * which map they are on, so a kept mover is a route request that raises rather
   * than a route that is merely wrong.
   */
  readonly mover: Mover;
  /**
   * Where the server put this seat, as a continuous position — the new floor's
   * entrance.
   *
   * Known before any snapshot of the new floor has arrived, because the room
   * reseats every player on the entrance in the same handler that rewrites the
   * level (`descent.room.ts`, `advanceTo`). Seeding from it rather than waiting
   * means the first frame after a descent draws the party arriving instead of
   * standing wherever they were on the floor above.
   */
  readonly spawn: Vec2;
}

/**
 * The same session, on a different floor.
 *
 * A descent is the one event this client has that **replaces the world under a
 * live session**. Every map here was built once and only ever grew by arrival
 * before it, so what this function is really about is the four things that must
 * *not* cross:
 *
 *   - **Every interpolation buffer**, and this is the load-bearing one. The room
 *     mints monster ids per floor from `m0` up (`world/monsters.ts`), so the new
 *     floor's first body is filed under the old floor's first body's key — and
 *     `receiveSnapshot` appends to whatever buffer it finds there. Kept, the new
 *     monster is interpolated from where the old one was standing: a body
 *     gliding through solid rock, which is `TODO.md` #201's recycled-id hazard
 *     arriving for real. The party's own buffers go for the same reason, since
 *     every seat was reseated on the new entrance.
 *   - **The floor's population and its loot.** A monster is the floor's and the
 *     room stops replicating it; a drop *stays behind*, which is
 *     `docs/GAME-DESIGN.md`'s own rule about what a run loses.
 *   - **What everybody was doing.** An action is the newest word about a body
 *     on a floor nobody is on any more.
 *   - **The clicks predicted upstairs.** They name cells of another grid, and
 *     replaying one against the new mover would walk the player somewhere
 *     nobody asked for.
 *
 * And two things that must cross, both of them counters the *room* keeps across
 * a descent. **The sequence counter**: `PlayerState.lastSequence` is not reset
 * when the floor is — the room acknowledges the `descend` on it — so a client
 * that restarted at `FIRST_INTENT_SEQUENCE` would have every intent afterwards
 * refused as stale until it climbed back past the descent's own number. And
 * **the clock**: one room has one tick counter, `state.tick` does not restart,
 * and throwing the estimate away would put the whole floor back behind a
 * resync.
 */
export const enterFloor = (session: Session, { mover, spawn }: FloorArrival): Session => {
  const fresh = createPrediction(
    mover,
    {
      tick: session.prediction.tick,
      position: spawn,
      // Standing, not walking: the server reseats a body, it does not send it
      // anywhere, and a destination carried down from the floor above would be
      // a cell of the wrong grid.
      destination: null,
      lastSequence: session.prediction.acknowledged,
    },
    session.prediction.motion.walker.speed,
  );

  return {
    ...session,
    mover,
    prediction: { ...fresh, nextSequence: session.prediction.nextSequence },
    remotes: new Map(),
    monsters: new Map(),
    actions: new Map(),
    ground: new Map(),
  };
};

/**
 * Takes the next intent sequence without predicting anything.
 *
 * A cast and a skill-point spend are intents like a click, and they are
 * acknowledged the same way: `PlayerState.lastSequence` is **one field** for the
 * lot, and the room refuses anything at or below it as stale. So the counter has
 * to be shared — an intent stream counting on its own would have half its
 * messages refused as replays, and the acknowledgement could not say which of
 * two intents it answered (`@d2/schema`'s `AttackPayload`: *"a player produces
 * one stream of decisions and the acknowledgement is one field"*).
 *
 * What it deliberately does not do is walk. `ARCHITECTURE.md` allows cosmetic
 * prediction and forbids predicting a damage number, and a cast has nothing else
 * to predict: the mana, the roll and the blow are the server's, and the client
 * finds out what happened from the next snapshot. So this moves the counter and
 * touches nothing else — `motion`, `pending` and `history` come out identical.
 */
export const takeSequence = (session: Session): SessionSequence => ({
  session: {
    ...session,
    prediction: { ...session.prediction, nextSequence: session.prediction.nextSequence + 1 },
  },
  sequence: session.prediction.nextSequence,
});

/**
 * The body a hotkey aims at: the nearest monster this client is **drawing**, or
 * `null` when it is drawing none.
 *
 * A targeting affordance and not a rule, which is the whole reason it is allowed
 * to be here. Whether that body may actually be cast at — reach, line of sight,
 * hostility, life, the caster's cooldown and pool — is `admitCast`'s, in core,
 * on the server, and the client neither asks nor predicts it. What this answers
 * is the question a keyboard cannot: *which* of the bodies on screen the player
 * meant, which in D2 is answered by the cursor and here, for a three-skill
 * slice, by proximity.
 *
 * Monsters only. A party member is drawn the same way and is never a target: the
 * game has no friendly fire and `admitCast` would refuse it, so aiming at an
 * ally would be a hotkey that reliably does nothing.
 *
 * The id breaks a tie, for the reason `skill-tree-model.ts` breaks one: a `Map`
 * preserves insertion order, so without it two clients drawing the same two
 * bodies could aim at different ones depending on which snapshot introduced
 * them first.
 */
export const nearestTargetId = (positions: EntityPositions): string | null => {
  let bestId: string | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const [id, monster] of positions.monsters) {
    const away = distance(positions.local, monster.position);

    if (bestId === null || away < bestDistance || (away === bestDistance && id < bestId)) {
      bestId = id;
      bestDistance = away;
    }
  }

  return bestId;
};

/**
 * Stands the local player at a continuous position, cancelling any walk.
 *
 * **The renderer sandbox's operation, not the game's.** In a room the server
 * owns where this entity is and the only way it moves is a click, a step or a
 * correction; nothing here may put a player somewhere it did not walk to, and a
 * placement made against a live room is undone by the next snapshot as it
 * should be. It exists because the browser suite has to put the renderer in
 * arrangements a live room cannot produce on demand — an entity standing
 * between two particular rocks, two capsules a diagonal step apart — and a
 * canvas offers no other way in.
 */
export const placeLocal = (session: Session, position: Vec2): Session => ({
  ...session,
  prediction: { ...session.prediction, motion: placeAt(session.prediction.motion, position) },
});

/**
 * Stands another entity at a continuous position. The sandbox's counterpart to
 * `placeLocal`, and the same caveat: a room overwrites it on the next snapshot.
 *
 * The buffer is replaced rather than appended to, so the placement is a *jump*
 * and not a 100 ms glide from wherever that id was last seen — one sample is a
 * buffer that cannot interpolate, which `interpolation.ts` answers by drawing
 * that sample exactly.
 */
export const placeRemote = (session: Session, id: string, position: Vec2): Session => {
  const remotes = new Map(session.remotes);
  remotes.set(id, receiveSample(createBuffer(), { tick: 0, position }));

  return { ...session, remotes };
};

/** Drops an entity and everything known about where it was going. */
export const forgetRemote = (session: Session, id: string): Session => {
  const remotes = new Map(session.remotes);
  remotes.delete(id);

  return { ...session, remotes };
};

/**
 * Says what a player is doing. `placeRemote`'s counterpart for the fifth thing
 * the wire replicates, and the same sandbox-only caveat: a room owns what its
 * seats are doing and the next snapshot overwrites anything put here.
 *
 * It exists because three of the seven states cannot be asked of a live room at
 * all. **`blocking` never fires**: no stat block on this floor carries a block
 * rating and no rule spends frames on a blocked swing (`TODO.md` #32), so the
 * only way to see the art that will be right the day it lands is to stage it.
 * **`casting` is invisible for eight of the nine skills**, because D2's casting
 * delay genuinely is zero for them (`TODO.md` #483). And a **corpse** is a
 * player death, which is not a thing a browser test can produce on demand
 * without spending a character.
 *
 * The local player is named like anybody else: `actions` is keyed by session id
 * and the local id is in it, which is what lets the sandbox lay this client's
 * own body out on the floor.
 */
export const actEntity = (session: Session, id: string, action: BodyAction): Session => {
  const actions = new Map(session.actions);
  actions.set(id, action);

  return { ...session, actions };
};

/**
 * Stands a monster on the floor, exactly as described. `placeRemote`'s
 * counterpart, and the same sandbox-only caveat: a live room owns its population
 * and the next snapshot overwrites anything put here.
 *
 * It takes a whole `MonsterSnapshot` rather than a position because a monster is
 * *described* as well as placed — a champion, a boss, a body at a quarter life —
 * and the browser suite has to be able to arrange each of those on demand. Like
 * `placeRemote` it replaces the buffer, so a second call is a jump and not a
 * 100 ms glide.
 */
export const placeMonster = (session: Session, monster: MonsterSnapshot): Session => {
  const monsters = new Map(session.monsters);

  monsters.set(monster.id, {
    identity: identityOf(monster),
    buffer: receiveSample(createBuffer(), { tick: 0, position: monster.position }),
  });

  return { ...session, monsters };
};

/** Takes a monster off the floor. What a death looks like from here — see `receiveSnapshot`. */
export const forgetMonster = (session: Session, id: string): Session => {
  const monsters = new Map(session.monsters);
  monsters.delete(id);

  return { ...session, monsters };
};

/**
 * Lays a drop on the floor. `placeMonster`'s counterpart for the fourth thing a
 * room replicates, and the same sandbox-only caveat: a live room owns what is
 * lying on its floor and the next snapshot overwrites anything put here.
 *
 * It takes the whole snapshot rather than a cell for the reason `placeMonster`
 * does: what has to be arranged on demand is a *unique*, a pile of coins, a
 * respec scroll — and none of those can be asked of a room, because a drop is
 * the outcome of a kill and a roll.
 */
export const placeGroundItem = (session: Session, item: GroundItemSnapshot): Session => {
  const ground = new Map(session.ground);
  ground.set(item.id, item);

  return { ...session, ground };
};

/** Takes a drop off the floor. What a pickup — anybody's — looks like from here. */
export const forgetGroundItem = (session: Session, id: string): Session => {
  const ground = new Map(session.ground);
  ground.delete(id);

  return { ...session, ground };
};
